import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";

const AdminDashboard = () => {
  const { logout } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sortField, setSortField] = useState("timestamp"); // timestamp, name, country
  const [sortOrder, setSortOrder] = useState("desc"); // asc or desc
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedInquiry, setSelectedInquiry] = useState(null); // for modal

  useEffect(() => {
    const stored = localStorage.getItem("contact_inquiries");
    if (stored) {
      setInquiries(JSON.parse(stored));
    }
  }, []);

  const markAsRead = (id) => {
    const updated = inquiries.map(inq =>
      inq.id === id ? { ...inq, read: true } : inq
    );
    setInquiries(updated);
    localStorage.setItem("contact_inquiries", JSON.stringify(updated));
  };

  const deleteInquiry = (id) => {
    const updated = inquiries.filter(inq => inq.id !== id);
    setInquiries(updated);
    localStorage.setItem("contact_inquiries", JSON.stringify(updated));
    if (selectedInquiry?.id === id) setSelectedInquiry(null);
  };

  // Filtering
  const filteredInquiries = inquiries.filter(inq => {
    if (filter === "read") return inq.read === true;
    if (filter === "unread") return !inq.read;
    return true;
  });

  // Sorting
  const sortedInquiries = [...filteredInquiries].sort((a, b) => {
    let valA, valB;
    if (sortField === "timestamp") {
      valA = new Date(a.timestamp);
      valB = new Date(b.timestamp);
    } else if (sortField === "name") {
      valA = a.fullName.toLowerCase();
      valB = b.fullName.toLowerCase();
    } else if (sortField === "country") {
      valA = (a.country || "").toLowerCase();
      valB = (b.country || "").toLowerCase();
    }
    if (valA < valB) return sortOrder === "asc" ? -1 : 1;
    if (valA > valB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedInquiries.length / pageSize);
  const paginatedInquiries = sortedInquiries.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="ml-1 text-gray-400">↕️</span>;
    return sortOrder === "asc" ? <span className="ml-1">↑</span> : <span className="ml-1">↓</span>;
  };

  return (
    <div className="min-h-screen bg-spice-cream py-8">
      <div className="container-custom">
        <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
          <h1 className="text-3xl md:text-4xl font-playfair font-bold text-primary">
            Admin Dashboard
          </h1>
          <button
            onClick={logout}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
          >
            Logout
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
            <h2 className="text-2xl font-semibold">Contact Inquiries</h2>
            <div className="flex gap-2">
              <button
                onClick={() => setFilter("all")}
                className={`px-3 py-1 rounded ${filter === "all" ? "bg-primary text-white" : "bg-gray-200"}`}
              >
                All
              </button>
              <button
                onClick={() => setFilter("unread")}
                className={`px-3 py-1 rounded ${filter === "unread" ? "bg-primary text-white" : "bg-gray-200"}`}
              >
                Unread
              </button>
              <button
                onClick={() => setFilter("read")}
                className={`px-3 py-1 rounded ${filter === "read" ? "bg-primary text-white" : "bg-gray-200"}`}
              >
                Read
              </button>
            </div>
          </div>

          {/* Pagination size control */}
          <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show:</span>
              {[10, 50, 100].map(size => (
                <button
                  key={size}
                  onClick={() => handlePageSizeChange(size)}
                  className={`px-3 py-1 rounded text-sm ${pageSize === size ? "bg-primary text-white" : "bg-gray-200"}`}
                >
                  {size}
                </button>
              ))}
            </div>
            <div className="text-sm text-gray-600">
              Showing {paginatedInquiries.length} of {sortedInquiries.length}
            </div>
          </div>

          {sortedInquiries.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No inquiries found.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        onClick={() => handleSort("timestamp")}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        Date <SortIcon field="timestamp" />
                      </th>
                      <th
                        onClick={() => handleSort("name")}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        Name <SortIcon field="name" />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th
                        onClick={() => handleSort("country")}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        Country <SortIcon field="country" />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telephone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedInquiries.map((inq) => (
                      <tr
                        key={inq.id}
                        className={`${!inq.read ? "bg-yellow-50" : ""} cursor-pointer hover:bg-gray-50`}
                        onClick={() => setSelectedInquiry(inq)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(inq.timestamp).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{inq.fullName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{inq.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{inq.country || "-"}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{inq.telephone || "-"}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${inq.read ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {inq.read ? "Read" : "Unread"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                          {!inq.read && (
                            <button
                              onClick={() => markAsRead(inq.id)}
                              className="text-indigo-600 hover:text-indigo-900 mr-3"
                            >
                              Mark read
                            </button>
                          )}
                          <button
                            onClick={() => deleteInquiry(inq.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination controls */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-6">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p-1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Inquiry Detail Modal */}
      <AnimatePresence>
        {selectedInquiry && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedInquiry(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h3 className="text-xl font-playfair font-semibold text-primary">Inquiry Details</h3>
                <button
                  onClick={() => setSelectedInquiry(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase">Date</label>
                    <p className="text-gray-900">{new Date(selectedInquiry.timestamp).toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase">Status</label>
                    <p className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${selectedInquiry.read ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {selectedInquiry.read ? "Read" : "Unread"}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase">Full Name</label>
                    <p className="text-gray-900">{selectedInquiry.fullName}</p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase">Email</label>
                    <p className="text-gray-900">{selectedInquiry.email}</p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase">Country</label>
                    <p className="text-gray-900">{selectedInquiry.country || "Not provided"}</p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase">Telephone</label>
                    <p className="text-gray-900">{selectedInquiry.telephone || "Not provided"}</p>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Message</label>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                    {selectedInquiry.message}
                  </p>
                </div>
              </div>
              <div className="border-t px-6 py-4 flex justify-end gap-3">
                {!selectedInquiry.read && (
                  <button
                    onClick={() => {
                      markAsRead(selectedInquiry.id);
                      setSelectedInquiry({ ...selectedInquiry, read: true });
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Mark as Read
                  </button>
                )}
                <button
                  onClick={() => {
                    deleteInquiry(selectedInquiry.id);
                    setSelectedInquiry(null);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
                <button
                  onClick={() => setSelectedInquiry(null)}
                  className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;